<template>
  <div>
    <v-container>
      <v-row class="wz-client-filters">
        <v-col cols="6">
          <v-text-field v-model="search" dense outlined clearable placeholder="Search"/>
        </v-col>
        <v-col cols="6">
          <v-checkbox v-model="showDisabled" dense label="Show Disabled" class="float-left pr-5"></v-checkbox>
          <v-checkbox v-model="showOnlyCodeVersionNotWorking" dense label="Show Only Code Not Working"
                      class="float-left"></v-checkbox>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-data-table :headers="headers" :items="filteredClients" :search="search">
            <template v-slot:[`item.emailTo`]="{ item }">
              <td>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <span class="d-inline-block text-truncate" style="max-width: 150px;">{{ item.emailTo }}</span>
                    </span>
                  </template>
                  <span>
                    {{ item.emailTo }}
                  </span>
                </v-tooltip>
              </td>
            </template>
            <template v-slot:[`item.maxNumberOfSeats`]="{ item }">
              <td>
                {{ !item.maxNumberOfSeats ? 'N/A' : item.maxNumberOfSeats }}
              </td>
            </template>
            <template v-slot:[`item.systemScore`]="{ item }">
              <td>
                <v-progress-circular v-if="chatRatingsLoading" indeterminate size="32"/>
                <client-health-score-icon v-if="!chatRatingsLoading" :item="item"
                                          @click="openHealthScoreDetails(item.clientId)"/>
              </td>
            </template>
            <template v-slot:[`item.satisfactionLevel`]="{ item }">
              <td>
                <v-btn icon @click="openHealthScoreDetails(item.clientId)">
                  <health-score-icon :satisfaction-level="item.satisfactionLevel"/>
                </v-btn>
              </td>
            </template>
            <template v-slot:[`item.satisfactionLevelLastUpdated`]="{ item }">
              <td>
                {{ dateToLocaleTimeDateString(item.satisfactionLevelLastUpdated) }}
              </td>
            </template>
            <template v-slot:[`item.domains`]="{ item }">
              <a :key="domain" v-for="domain in item.domains.split(',')" :href="'https://' + domain" class="mr-1"
                 target="_blank">{{ domain }}</a>
            </template>
            <template v-slot:[`item.disabled`]="{ item }">
              <v-tooltip bottom v-if="item.disabled">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on"><v-icon color="error">mdi-close</v-icon></span>
                </template>
                <span>This client is disabled</span>
              </v-tooltip>
              <v-tooltip bottom v-else>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on"><v-icon color="primary">mdi-check</v-icon></span>
                </template>
                <span>This client is enabled</span>
              </v-tooltip>
            </template>
            <template v-slot:[`item.script`]="{ item }">
              <v-tooltip bottom v-if="separateWhizeoScript(item.codeVersion)">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn @click="viewScript(item)" icon color="info" v-bind="attrs"
                         v-on="on">
                    <v-icon>mdi-script-outline</v-icon>
                  </v-btn>
                </template>
                <span>This client requires a separate Whizeo script. Click to view</span>
              </v-tooltip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn @click="editClient(item)" icon color="primary" :disabled="selectedClient.loading && selectedClient.clientId !== item.clientId"
                     :loading="selectedClient.loading && selectedClient.clientId === item.clientId">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <template v-slot:[`item.codeVersion`]="{ item }">
              <v-btn @click="editCodeVersion(item)" outlined text x-small color="black">
                {{ item.codeVersion || '-' }}
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-btn fab fixed bottom right color="primary" @click="dialogAdd = true">
        <v-icon>mdi-plus</v-icon>
      </v-btn>

      <v-dialog v-model="dialogAdd" max-width="500px">
        <v-form ref="formAddClient" @submit.prevent="addClient">
          <v-card>
            <v-app-bar color="secondary" dark flat dense>
              <span>Add Client</span>
              <v-spacer/>
              <v-btn icon dark @click="dialogAdd = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-app-bar>
            <v-card-text class="pt-6">
              <v-row>
                <v-col cols="9">
                  <v-text-field ref="lfClientId" v-model="newClient.lfClientId" label="Lead Forensics Client ID"
                                placeholder="Lead Forensics Client ID" outlined dense clearable required
                                :rules="[v => !!v || 'Please enter the LF client ID']"
                                @keydown.enter="validateLfClient"/>
                </v-col>
                <v-col cols="3">
                  <v-btn color="primary" @click="validateLfClient">Validate</v-btn>
                </v-col>
              </v-row>
              <v-row v-if="newClient.valid">
                <v-col cols="12">
                  <v-text-field ref="name" v-model="newClient.name" label="Name" placeholder="Name" dense clearable
                                required :rules="[v => !!v || 'Please enter the name']"/>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="newClient.domains" label="Domain" placeholder="Domain" dense clearable required
                                :rules="[v => !!v || 'Please enter the client domain']"/>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="newClient.emailTo" label="Email To" placeholder="Email To" dense clearable
                                required :rules="[v => !!v || 'Please enter the email recipient']"/>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="newClient.emailCc" label="Email CC" placeholder="Email CC" dense clearable/>
                </v-col>
                <v-col v-if="!newClientHasUnlimitedNumberOfSeats" cols="12">
                  <v-text-field v-model="newClient.maxNumberOfSeats" label="Max Number Of Seats"
                                placeholder="Max Number Of Seats" dense clearable
                                :rules="[v => !!v || 'Please enter max number of seats!']"/>
                </v-col>
                <v-col cols="12">
                  <v-checkbox v-model="newClientHasUnlimitedNumberOfSeats" label="Unlimited number of seats" dense/>
                </v-col>
                <v-col cols="12">
                  <v-checkbox v-model="newClient.isLfClient" label="LF Client" dense/>
                </v-col>
                <v-col v-if="separateWhizeoScript(newClient.codeVersion)">
                  <strong>Note:</strong>
                  This client is using a custom LF code version and will require a separate Whizeo script to be added to
                  their website.
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-btn type="submit" :disabled="!newClient.valid || loading" color="primary" block>Create</v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-dialog>

      <v-dialog v-model="dialogEdit" max-width="500px">
        <v-form ref="formEditClient" @submit.prevent="updateClient">
          <v-card>
            <v-app-bar color="secondary" dark flat dense>
              <span>Edit Client</span>
              <v-spacer/>
              <v-btn icon dark @click="dialogEdit = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-app-bar>
            <v-card-text class="pt-6">
              <v-row>
                <v-col cols="12">
                  <v-text-field ref="name" v-model="selectedClient.name" label="Name" placeholder="Name" dense clearable
                                required :rules="[v => !!v || 'Please enter the name']"/>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="selectedClient.domains" label="Domain" placeholder="Domain" dense clearable
                                required :rules="[v => !!v || 'Please enter the client domain']"/>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="selectedClient.emailTo" label="Email To" placeholder="Email To" dense clearable
                                required :rules="[v => !!v || 'Please enter the email recipient']"/>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="selectedClient.emailCc" label="Email CC" placeholder="Email CC" dense
                                clearable/>
                </v-col>
                <v-col cols="12" v-if="!currentClientHasUnlimitedNumberOfSeats">
                  <v-text-field v-model.number="selectedClient.maxNumberOfSeats" label="Number of Seats"
                                placeholder="Number of Seats" dense clearable
                                :rules="[v => !!v || 'Please enter the number of seats']"/>
                </v-col>
                <v-col cols="12">
                  <v-checkbox v-model="currentClientHasUnlimitedNumberOfSeats" label="Unlimited number of seats" dense/>
                </v-col>
                <v-col cols="12">
                  <v-checkbox v-model="selectedClient.disabled" label="Disabled" dense/>
                </v-col>
                <v-col cols="12">
                  <v-checkbox v-model="selectedClient.doNotStoreBadEmailLeads" label="Do Not Allow Bad Emails" dense/>
                </v-col>
                <v-col cols="12">
                  <v-checkbox v-model="selectedClient.isLfClient" label="LF Client" dense/>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-btn type="submit" color="primary" block :loading="loading">Update</v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-dialog>

      <v-dialog v-model="dialogScript" max-width="500px">
        <v-card>
          <v-app-bar color="secondary" dark flat dense>
            <span>Whizeo Script</span>
            <v-spacer/>
            <v-btn icon dark @click="dialogScript = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-app-bar>
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12">
                <v-textarea :value="whizeoScript(selectedClient.clientId)" outlined dense hide-details ref="script"/>
              </v-col>
              <v-col cols="12">
                Highlight the script and press Ctrl-C to copy
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogCodeVersion" max-width="500px">
        <v-form ref="formEditClient" @submit.prevent="updateCodeVersion">
          <v-card>
            <v-app-bar color="secondary" dark flat dense>
              <span>Edit code version</span>
              <v-spacer/>
              <v-btn icon dark @click="dialogCodeVersion = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-app-bar>
            <v-card-text class="pt-6">
              <v-row>
                <v-col cols="12">
                  Client ID: <b>{{ selectedClient.clientId }}</b><br/>
                  Client Name: <b>{{ selectedClient.name }}</b>
                </v-col>
                <v-col cols="12">
                  <v-select
                    :items="getCodeVersionList()"
                    label="Code version"
                    solo
                    v-model="selectedClient.codeVersion"
                  >
                  </v-select>
                </v-col>
                <v-col cols="12">
                  <v-btn type="submit" color="primary" block>Save</v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-form>
      </v-dialog>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" dark>
        {{ snackbar.message }}
      </v-snackbar>

    </v-container>
  </div>
</template>

<script>
import api from "../services/support.api";
import {mapActions, mapState} from "vuex";
import {dateToLocaleTimeDateString, dateToTimeDateString} from "../helpers/dateHelper";
import {LoadingState} from "@/store/constants";
import HealthScoreIcon from "@/components/HealthScore/CustomerOverview/HealthScoreIcon.vue";
import ClientHealthScoreIcon from "@/components/Clients/ClientHealthScoreIcon.vue";
import {VERSIONS} from "../setup/codeVersions";
import leadsApi from "../services/leads.api";
import i18n from '../setup/locale/en';

export default {
  components: {ClientHealthScoreIcon, HealthScoreIcon},
  data: () => ({
    search: '',
    showDisabled: false,
    showOnlyCodeVersionNotWorking: false,
    headers: [
      {text: 'ClientID', align: 'start', value: 'clientId'},
      {text: 'LF Client ID', value: 'lfClientId'},
      {text: 'Name', value: 'name'},
      {text: 'Domains', value: 'domains'},
      {text: 'Email To', value: 'emailTo',},
      {text: 'Code version', value: 'codeVersion'},
      {text: 'Master User Name', value: 'masterUserName'},
      {text: 'Number of Seats', value: 'maxNumberOfSeats'},
      {
        text: 'System score',
        value: 'systemScore',
        sortable: true,
        sort: (a, b) => {
          // handle clients without ratings calculated
          if (a === undefined) {
            return -1;
          }
          if (b === undefined) {
            return 1;
          }
          // handle null values (report exists but score is null so code is not working)
          const x = a === null ? -1 : a;
          const y = b === null ? -1 : b;
          // compare score values in standard way
          if (x > y) {
            return 1;
          } else if (x < y) {
            return -1;
          } else {
            return 0;
          }
        }
      },
      {text: 'CSM Sentiment', value: 'satisfactionLevel'},
      {text: 'CSM Sentiment - Last Updated', value: 'satisfactionLevelLastUpdated'},
      {text: '', value: 'disabled', width: '50px'},
      {text: '', value: 'script', sortable: false, width: '50px'},
      {text: '', value: 'actions', sortable: false, width: '50px'},
    ],
    dialogAdd: false,
    dialogEdit: false,
    dialogScript: false,
    dialogCodeVersion: false,
    newClient: {
      lfClientId: null,
      valid: false,
      name: '',
      domains: '',
      emailTo: '',
      emailCc: '',
      maxNumberOfSeats: null,
      codeVersion: null,
      isLfClient: false,
    },
    selectedClient: {
      clientId: null,
      name: '',
      domains: '',
      emailTo: '',
      emailCc: '',
      maxNumberOfSeats: null,
      script: '',
      disabled: false,
      codeVersion: null,
      isLfClient: false,
    },
    newClientHasUnlimitedNumberOfSeats: true,
    currentClientHasUnlimitedNumberOfSeats: null,
    snackbar: {
      show: false,
      color: "primary",
    },
    loading: false,
  }),
  watch: {
    dialogAdd: function () {
      if (!this.dialogAdd) {
        this.newClient = {
          lfClientId: null,
          valid: false,
          name: '',
          domains: '',
          emailTo: '',
          emailCc: '',
          maxNumberOfSeats: null,
          codeVersion: null,
          isLfClient: false,
        };

        // Dialog is closed, so we need to set this to true, as newClient is an object with empty / false / null fields and unlimited number of seats is true by default but could have been set to false in the previous add client
        this.newClientHasUnlimitedNumberOfSeats = true;
      } else {
        setTimeout(() => {
          this.$refs.lfClientId.focus();
        }, 100);
      }
    },
    dialogEdit: function () {
      if (!this.dialogEdit) {
        this.selectedClient = {
          clientId: null,
          name: '',
          domains: '',
          emailTo: '',
          emailCc: '',
          disabled: false,
          maxNumberOfSeats: null,
          script: '',
          isLfClient: false,
        };

        // Dialog is closed, so we need to set this to true, as selectedClient is an object with empty / false / null fields and unlimited number of seats is set by the maxNumberOfSeats field in the selected client object
        this.currentClientHasUnlimitedNumberOfSeats = null;
      } else {
        setTimeout(() => {
          this.$refs.lfClientId.focus();
        }, 100);
      }
    },
  },
  methods: {
    ...mapActions('clientStore', ['SET_CLIENT']),
    filterDisabled(client) {
      return this.showDisabled || !client.disabled
    },
    filterCodeWorking(client) {
      return !this.showOnlyCodeVersionNotWorking || (client.codeWorking != null && !client.codeWorking)
    },
    dateToLocaleTimeDateString,
    ...mapActions('clientStore', ['fetchClients', 'fetchClientsHealthScore', 'setClientLoadingState', 'setClientDoNotStoreBadEmailLeadsFilter']),
    openHealthScoreDetails(clientId) {
      this.$router.push(`/health-score/${clientId}`);
    },
    async getClientList() {
      await this.fetchClients()
    },
    getCodeVersionList() {
      return [
        {value: null, text: 'NOT SET'},
        ...VERSIONS.map(v => ({text: `${v.id} - ${v.name}`, value: v.id}))]
    },
    async validateLfClient() {
      if (isNaN(this.newClient.lfClientId)) {
        this.showSnackbar('error', 'LF client ID must be a number');
        this.newClient.valid = false;
        return;
      }
      try {
        const client = await api.validateLfClient(parseInt(this.newClient.lfClientId));
        if (!client.clientId) {
          this.showSnackbar('warning', 'LF client not found');
          this.newClient.valid = false;
          return;
        }
        if (client.alreadyExists) {
          this.showSnackbar('warning', 'This client is already configured in Whizeo');
          this.newClient.valid = false;
          return;
        }
        this.newClient.name = client.name;
        this.newClient.domains = client.domain;
        this.newClient.codeVersion = client.codeVersion;
        this.newClient.valid = true;
        setTimeout(() => {
          this.$refs.name.focus();
        }, 100);
      } catch (e) {
        this.showSnackbar('error', e);
      }
    },
    separateWhizeoScript(codeVersion) {
      return codeVersion === null || codeVersion !== 19;
    },
    showSnackbar(type, message) {
      this.snackbar.color = type;
      this.snackbar.message = message;
      this.snackbar.show = true;
    },
    async addClient() {
      if (!this.$refs.formAddClient.validate()) {
        return;
      }
      try {
        const maxNumberOfSeats = this.newClientHasUnlimitedNumberOfSeats ? null : this.newClient.maxNumberOfSeats;
        this.loading = true;
        const result = await api.createClient(
          this.newClient.name,
          this.newClient.lfClientId,
          this.newClient.domains,
          this.newClient.emailTo,
          this.newClient.emailCc,
          maxNumberOfSeats,
          this.newClient.isLfClient
        );

        this.showSnackbar('success', `Client ${result.clientId} created successfully`);
        this.dialogAdd = false;
        await this.getClientList();
        this.loading = false;
      } catch (e) {
        this.loading = false;
        this.showSnackbar('error', e);
      }
    },
    async editClient(client) {
      try {
        this.selectedClient = {
          clientId: client.clientId,
          name: client.name,
          domains: client.domains,
          emailTo: client.emailTo,
          emailCc: client.emailCc,
          maxNumberOfSeats: client.maxNumberOfSeats,
          disabled: client.disabled,
          loading: true,
          isLfClient: client.isLfClient
        };
        const clientDoesNotHaveBadEmailsFilter = client.doNotStoreBadEmailLeads === null;
        let doNotStoreBadEmailLeads;
        if (clientDoesNotHaveBadEmailsFilter) {
          doNotStoreBadEmailLeads = await leadsApi.getBadEmailsFilterByClient(client.clientId);
          await this.setClientDoNotStoreBadEmailLeadsFilter(
            {
              clientId: client.clientId,
              doNotStoreBadEmailLeads
            });
        } else {
          doNotStoreBadEmailLeads = client.doNotStoreBadEmailLeads;
        }

        this.selectedClient.doNotStoreBadEmailLeads = doNotStoreBadEmailLeads;
        this.currentClientHasUnlimitedNumberOfSeats = !this.selectedClient.maxNumberOfSeats;
        this.selectedClient.loading = false;
        this.dialogEdit = true;
      } catch (error) {
        this.showSnackbar('error', i18n.clients.editClient.error);
        this.selectedClient = {};
      }

    },
    editCodeVersion(client) {
      this.selectedClient = {
        clientId: client.clientId,
        name: client.name,
        codeVersion: client.codeVersion,
      };
      this.dialogCodeVersion = true
    },
    async updateClient() {
      if (!this.$refs.formEditClient.validate()) {
        return;
      }
      try {
        this.loading = true;
        const maxNumberOfSeats = this.currentClientHasUnlimitedNumberOfSeats ? null : this.selectedClient.maxNumberOfSeats

        await api.updateClient(
          this.selectedClient.clientId,
          this.selectedClient.name,
          this.selectedClient.domains,
          this.selectedClient.emailTo,
          this.selectedClient.emailCc,
          this.selectedClient.disabled,
          maxNumberOfSeats,
          this.selectedClient.isLfClient,
        );

        const client = this.clients.find((x) => x.clientId === this.selectedClient.clientId);

        // This update goes to whizeo-chat-api instead of client-user-api
        if (client.doNotStoreBadEmailLeads !== this.selectedClient.doNotStoreBadEmailLeads) {
          await leadsApi.toggleBadEmailsFilterByClient(client.clientId);
          await this.setClientDoNotStoreBadEmailLeadsFilter({
            clientId: this.selectedClient.clientId,
            doNotStoreBadEmailLeads: this.selectedClient.doNotStoreBadEmailLeads
          })
        }

        this.showSnackbar('success', i18n.clients.updateClient.success);

        client.name = this.selectedClient.name;
        client.domains = this.selectedClient.domains;
        client.emailTo = this.selectedClient.emailTo;
        client.emailCc = this.selectedClient.emailCc;
        client.disabled = this.selectedClient.disabled;
        client.maxNumberOfSeats = maxNumberOfSeats;
        client.isLfClient = this.selectedClient.isLfClient;
        this.SET_CLIENT(client);
        this.selectedClient = {};
        this.dialogEdit = false;
      } catch (e) {
        this.showSnackbar('error', i18n.clients.updateClient.error);
      } finally {
        this.loading = false;
      }
    },
    async updateCodeVersion() {
      const codeVersion = this.selectedClient.codeVersion
      const clientId = this.selectedClient.clientId
      await api.updateCodeVersion(clientId, codeVersion)
      const client = this.clients.find((x) => x.clientId === clientId);
      if (client) {
        client.codeVersion = codeVersion
      }
      this.dialogCodeVersion = false
      this.showSnackbar('success', 'Code version updaded successfullly');
    },
    viewScript(client) {
      this.selectedClient = {
        clientId: client.clientId,
        name: client.name,
        domains: client.domains,
        emailTo: client.emailTo,
        disabled: client.disabled,
      };
      this.dialogScript = true;
    },
    whizeoScript(clientId) {
      return `<script type="text/javascript" src="https://data.whizeo.com/widgets/loader.js?client=${clientId}" async><\/script>`;
    },
  },
  computed: {
    ...mapState('clientStore', ['clients', 'clientsHealthScores']),
    chatRatingsLoading() {
      return this.clientsHealthScores.state === LoadingState.LOADING;
    },
    filteredClients() {
      return this.clients.filter(this.filterDisabled).filter(this.filterCodeWorking)
    },
  },
  filters: {
    formatDate: function (value) {
      return dateToTimeDateString(value);
    }
  },
  async mounted() {
    await this.getClientList();
    this.fetchClientsHealthScore();
  }
}
</script>

<style scoped>
.wz-client-filters {
  position: fixed;
  top: 36px;
  right: 36px;
  left: 36px;
  margin: auto;
  z-index: 100;
}
</style>
